/**
 * usePermissions - what the signed-in person may do in the CHOSEN company.
 *
 * v4.28 - 2026-10-13 - G10-B (TEAM-ACCESS-ROLES-01, plan v2 D5; UI-PERMISSION-LIST-NOT-ENFORCED-01):
 *   The portal no longer holds its own copy of the role rules. The server's /api/me answers with the role
 *   and the permission map of the caller in the chosen company; the layout reads it ONCE (setMeSnapshot) and
 *   every screen and the menu read it from here. A key the server did not send is false (fail closed).
 *   The role matrix, role names and role descriptions that lived here are gone (names come from the
 *   translation files, rules from the server).
 * v4.28 - 2026-10-13 - G10-B (final G10-A contract): the 409 company_choice_required body carries the companies (no cache);
 *                      /api/me for a person with no company and a pending team invite answers 200 with pendingInvite;
 *                      503 company lookups publish 'unavailable'
 * v4.28 - 2026-10-13 - G10-B (Branko approval 2026-10-13): pendingInvite may carry acceptPath ("/invite/{token}"), sent by the
 *                      server only when the signed-in verified email equals the invite's email. It is the person's own accept
 *                      page; it is never logged (decisions log the inviteId only).
 * v4.30 - 2026-10-15 - ROW 69 (ruling 10): MeOutcome 'setup_incomplete' - the server says the company record has no workflow state
 * v2.99 - 2026-02-07 - Added manageWebhooks permission (owner/admin only)
 * v2.33 - 2026-01-31 - Initial implementation
 */
import { useSyncExternalStore } from 'react';
import { getChosenCompany, setChosenCompany, logClientDecision } from '../services/portalRequest';
import { isSafeReturnPath } from '../services/authGate';

export interface MeCompany {
  tenantUuid: string;
  companyName: string;
  role: string;
  isOwn: boolean;
}

export interface MeSnapshot {
  userId: string;
  role: string;
  permissions: Record<string, boolean>;
  activeTenantUuid: string | null;
  companies: MeCompany[];
}

export interface PendingInvite {
  inviteId: string;
  companyName: string | null;
  /** The accept page of this invite, in-site only; null when the server did not send one (email not matched). */
  acceptPath: string | null;
}

// Shape the server promises: "/invite/{token}". Anything else is ignored (never navigate to an unexpected path).
const ACCEPT_PATH_SHAPE = /^\/invite\/[^/?#\s]+$/;
export const isAcceptPath = (path: unknown): path is string =>
  isSafeReturnPath(path) && ACCEPT_PATH_SHAPE.test(path);

/**
 * Where a person with no company and a pending invite goes: the server's acceptPath first, else the link remembered
 * from the email, else nowhere (the layout shows the translated "open the link from your email" message).
 */
export function pendingInviteTarget(invite: PendingInvite | null, remembered: string | null): string | null {
  if (invite && invite.acceptPath) return invite.acceptPath;
  return remembered && isSafeReturnPath(remembered) ? remembered : null;
}

export type MeOutcome = 'ok' | 'choice_required' | 'failed' | 'unavailable' | 'pending_invite' | 'setup_incomplete'; // v4.30 - ROW 69

let snapshot: MeSnapshot | null = null;
let outcome: MeOutcome = 'ok';
let choiceCompanies: MeCompany[] = [];
let pendingInvite: PendingInvite | null = null;
const listeners = new Set<() => void>();

/** Companies in the /api/me shape (also the shape of the 409 company_choice_required body). */
export function parseCompanies(raw: unknown): MeCompany[] {
  return Array.isArray(raw)
    ? raw
        .filter((c: any) => c && typeof c.tenantUuid === 'string')
        .map((c: any) => ({
          tenantUuid: c.tenantUuid,
          companyName: typeof c.companyName === 'string' ? c.companyName : '',
          role: typeof c.role === 'string' ? c.role : '',
          isOwn: c.isOwn === true,
        }))
    : [];
}

/** Build the snapshot from a /api/me body; null when the body does not carry a role and a permission map. */
export function snapshotFromMe(me: any): MeSnapshot | null {
  if (!me || typeof me !== 'object' || typeof me.role !== 'string' || !me.role) return null;
  if (!me.permissions || typeof me.permissions !== 'object') return null;
  const permissions: Record<string, boolean> = {};
  Object.keys(me.permissions).forEach((key) => { permissions[key] = me.permissions[key] === true; });
  const companies: MeCompany[] = parseCompanies(me.companies);
  return {
    userId: typeof me.userId === 'string' ? me.userId : '',
    role: me.role,
    permissions,
    activeTenantUuid: typeof me.activeTenantUuid === 'string' ? me.activeTenantUuid : null,
    companies,
  };
}

export function setMeSnapshot(
  next: MeSnapshot | null,
  nextOutcome: MeOutcome = 'ok',
  extra: { choiceCompanies?: MeCompany[]; pendingInvite?: PendingInvite | null } = {},
): void {
  snapshot = next;
  outcome = nextOutcome;
  choiceCompanies = extra.choiceCompanies || [];
  pendingInvite = extra.pendingInvite || null;
  listeners.forEach((listener) => listener());
}

/** 409 company_choice_required: the body carries the companies to choose from (same shape as /api/me). */
export function applyChoiceRequired(body: unknown): MeCompany[] {
  const companies = parseCompanies(body && typeof body === 'object' ? (body as { companies?: unknown }).companies : null);
  setMeSnapshot(null, 'choice_required', { choiceCompanies: companies });
  return companies;
}

/** The pending team invite of a person with no company: {inviteId, companyName, acceptPath?}. */
export function parsePendingInvite(raw: unknown): PendingInvite | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as { inviteId?: unknown; companyName?: unknown; acceptPath?: unknown };
  if (typeof value.inviteId !== 'string' || !value.inviteId) return null;
  return {
    inviteId: value.inviteId,
    companyName: typeof value.companyName === 'string' && value.companyName ? value.companyName : null,
    acceptPath: isAcceptPath(value.acceptPath) ? value.acceptPath : null,
  };
}

/**
 * The ONE place a /api/me answer is taken in. Keeps the chosen company (first time: the server's active company),
 * remembers the companies list and publishes role + permissions for every screen and the menu.
 * Returns null (and publishes the failure) when the answer carries no role and permission map: a role is never guessed.
 */
export function applyMeResponse(me: unknown, clerkUserId: string): MeSnapshot | null {
  // A person with no company and a pending team invite: 200 with companies [], role null, permissions {} and pendingInvite.
  const invite = me && typeof me === 'object' && !(me as { role?: unknown }).role ? parsePendingInvite((me as { pendingInvite?: unknown }).pendingInvite) : null;
  if (invite) {
    logClientDecision('pending_invite_found', { invite: invite.inviteId });
    setMeSnapshot(null, 'pending_invite', { pendingInvite: invite });
    return null;
  }
  const parsed = snapshotFromMe(me);
  if (!parsed) {
    logClientDecision('me_without_permissions', { company: getChosenCompany() || 'none' });
    setMeSnapshot(null, 'failed');
    return null;
  }
  if (!getChosenCompany() && parsed.activeTenantUuid) {
    setChosenCompany(parsed.activeTenantUuid, clerkUserId);
    logClientDecision('company_default_chosen', { company: parsed.activeTenantUuid });
  }
  const published = { ...parsed, userId: parsed.userId || clerkUserId };
  setMeSnapshot(published, 'ok');
  return published;
}

export function getMeOutcome(): MeOutcome {
  return outcome;
}

export function getChoiceCompanies(): MeCompany[] {
  return choiceCompanies;
}

export function getPendingInvite(): PendingInvite | null {
  return pendingInvite;
}

export function getMeSnapshot(): MeSnapshot | null {
  return snapshot;
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

/** True only when the server said yes for the chosen company. */
export function hasPermission(permission: string, from: MeSnapshot | null = snapshot): boolean {
  return !!from && from.permissions[permission] === true;
}

/**
 * Hook to read the permissions in components.
 * The argument is kept only so existing call sites compile; it is ignored (the server's answer is the one source).
 */
export function usePermissions(_legacyRole?: unknown) {
  const me = useSyncExternalStore(subscribe, getMeSnapshot, getMeSnapshot);
  const currentOutcome = useSyncExternalStore(subscribe, getMeOutcome, getMeOutcome);
  const choice = useSyncExternalStore(subscribe, getChoiceCompanies, getChoiceCompanies);
  const pending = useSyncExternalStore(subscribe, getPendingInvite, getPendingInvite);
  return {
    outcome: currentOutcome,
    choiceCompanies: choice,
    pendingInvite: pending,
    loaded: me !== null,
    userId: me ? me.userId : null,
    role: me ? me.role : null,
    permissions: me ? me.permissions : ({} as Record<string, boolean>),
    companies: me ? me.companies : ([] as MeCompany[]),
    activeTenantUuid: me ? me.activeTenantUuid : null,
    hasPermission: (permission: string) => hasPermission(permission, me),
  };
}

export default usePermissions;
